import { events } from "@constants/tracking";
import { useEditNameDialog } from "@contexts/DialogContext/dialogs/edit-name";
import { useEditUsernameDialog } from "@contexts/DialogContext/dialogs/edit-username";
import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import { useThemeContext } from "@contexts/ThemeContext";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { buildNumber, buildType } from "@helpers/app-identity";
import { useAppVersion } from "@helpers/app-version";
import { getEntitlements } from "@helpers/entitlements";
import i18n from "@helpers/lib/i18n";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useExams from "@hooks/useExams";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { defaultI18nConfig } from "@memorang/constants/config/defaults";
import { PROD_CORE_URL } from "@memorang/constants/urls";
import { isSafeUrl } from "@memorang/helpers/common";
import { formatDate } from "@memorang/helpers/date";
import { composeMessage } from "@memorang/helpers/email";
import { getCurrentLanguageName } from "@memorang/helpers/i18n";
import { isWeb } from "@memorang/helpers/platform";
import { getPlatform } from "@memorang/helpers/platform";
import { useCopyToClipboard } from "@memorang/ui/useCopyToClipboard";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { Linking } from "react-native";
import type { Section } from "../types";

const isDevelopment = process.env.NODE_ENV === "development";

export type DialogType =
	| "DATE_PICKER"
	| "SIGN_OUT"
	| "LANGUAGE"
	| "THEME"
	| "FAQS"
	| "FEEDBACK"
	| "APP_SELECTION";

type Callbacks = {
	toggleDialog: (type: DialogType) => void;
	toggleShowDeleteFeedbackDialog: () => void;
	toggleAudioPlaybackTestDialog: () => void;
	onRedeemPurchase: () => void;
};

const useAccountSettings = ({
	toggleDialog,
	toggleShowDeleteFeedbackDialog,
	toggleAudioPlaybackTestDialog,
	onRedeemPurchase,
}: Callbacks) => {
	const router = useRouter();
	const { t } = useTranslation();
	const { handleFeedbackNavigation } = useCustomNavigation();
	const { showPurchase } = usePurchaseDialog();
	const { showEditUsername } = useEditUsernameDialog();
	const { showEditName } = useEditNameDialog();
	const appVersion = useAppVersion();

	const {
		currentExamContext: { examDate, examName, examIcon },
	} = useExamContext();

	const { email, isAdmin, username, firstName, lastName } = useAuthContext();

	const adminPaywallEnabled = useGlobalStore(
		(state) => state.adminPaywallEnabled,
	);
	const setAdminPaywallEnabled = useGlobalStore(
		(state) => state.setAdminPaywallEnabled,
	);
	const resetGlobalStore = useGlobalStore((state) => state.resetGlobalStore);

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const { copyToClipboard } = useCopyToClipboard();

	const { themePreference } = useThemeContext();

	const exam = useAppStore((store) => store.exam);
	const supportEmail = useAppStore((store) => store.supportEmail);
	const aiToolsTosLink = useAppStore((store) => store.app.links.aiToolsTos);
	const contactSupportUrl = useAppStore(
		(store) => store.app.links.contactSupportUrl,
	);

	const privacyLink = useAppStore((store) => store.app.links.privacy);
	const showLanguageSettings = useAppStore(
		(store) => store.app.accountSettings?.showLanguageSettings,
	);
	const supportedLocales = useAppStore(
		(store) => (store.app.i18n ?? defaultI18nConfig).supportedLocales,
	);
	const showPurchasedProducts = useAppStore(
		(store) => store.app.accountSettings?.showPurchasedProducts,
	);

	const showFaqs = useAppStore((store) => store.app.accountSettings?.showFaqs);

	const showPartnersSection = useAppStore(
		(store) => store.app.accountSettings?.showPartnersSection,
	);

	const showAudioTestDialog = useAppStore(
		(store) => store.app.accountSettings?.showAudioTestDialog,
	);

	const allowChangeExam = useAppStore((store) => store.exam.allowChangeExam);
	const isTokenBasedFlow = useAppStore(
		(store) => store.app.auth?.tokenBasedFlow ?? false,
	);
	const canExtendSubscription = !isTokenBasedFlow;
	const tosLink = useAppStore((store) => store.app.links.tos);
	const { examsList } = useExams();

	const entitlements = getEntitlements(examsList);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const getAccountSections = (): Section[] => {
		return [
			...(exam?.enabled
				? [
						...(allowChangeExam
							? [
									{
										sectionTitle: t("account.currentExam"),
										items: [
											{
												icon: examIcon || "file-document-outline",
												type: "exam-filter",
												title: examName,
												iconRight: "swap-horizontal",
											},
										],
									},
								]
							: []),
						{
							sectionTitle: t("account.examDate"),
							items: [
								{
									icon: "calendar-outline",
									type: "datepicker",
									title: examDate
										? formatDate(examDate, i18n.language)
										: t("account.selectDate"),
									onPress: () => toggleDialog("DATE_PICKER"),
									iconRight: "pencil-outline",
								},
							],
						},
					]
				: []),
			...(entitlements.length > 0 && showPurchasedProducts
				? [
						{
							sectionTitle: t("account.subscriptions"),
							items: entitlements.map(
								({ name, icon, subscription, productType }) => {
									return {
										icon,
										...(canExtendSubscription
											? {
													type: "extend-subscription",
													onPress: () => showPurchase({ productType }),
												}
											: {}),
										title: name,
										description: `${t("account.validUntil", {
											date: formatDate(subscription.endDate),
										})}`,
									};
								},
							),
						},
					]
				: []),
			{
				sectionTitle: t("account.purchases"),
				items: [
					{
						onPress: onRedeemPurchase,
						icon: "wallet-giftcard",
						title: t("account.redeemPurchase"),
					},
				],
			},
			...(showAudioTestDialog || isAdmin
				? [
						{
							sectionTitle: t("account.features"),
							items: [
								...(showAudioTestDialog
									? [
											{
												icon: "volume-high",
												title: t("account.audioPlaybackTest"),
												type: "toggle-audio-playback",
												onPress: () => toggleAudioPlaybackTestDialog(),
											},
										]
									: []),
								...(isAdmin
									? [
											{
												icon: adminPaywallEnabled
													? "lock-open-outline"
													: "lock-outline",
												title: adminPaywallEnabled
													? "Hide paywall"
													: "Show paywall",
												type: "toggle-paywall",
												onPress: () =>
													setAdminPaywallEnabled(!adminPaywallEnabled),
												description: adminPaywallEnabled
													? "Paywall is enabled"
													: "Paywall is disabled",
											},
										]
									: []),
							],
						},
					]
				: []),
			{
				sectionTitle: t("account.appearance"),
				items: [
					{
						icon: "theme-light-dark",
						title: t("account.theme"),
						description: t(`account.themeOptions.${themePreference}`),
						iconRight: "chevron-right",
						onPress: () => toggleDialog("THEME"),
					},
					...((isDevelopment || Boolean(showLanguageSettings)) &&
					supportedLocales.length > 1
						? [
								{
									icon: "translate",
									title: t("language.sectionTitle"),
									onPress: () => toggleDialog("LANGUAGE"),
									description: getCurrentLanguageName(),
								},
							]
						: []),
				],
			},
			{
				sectionTitle: t("account.accountInfo"),
				items: [
					{
						icon: "account-outline",
						title: t("account.username"),
						description: username || "",
						iconRight: "pencil-outline",
						onPress: () => showEditUsername(),
					},
					{
						icon: "account-box-outline",
						title: t("account.name"),
						description:
							[firstName, lastName].filter(Boolean).join(" ") ||
							t("account.nameNotSet"),
						iconRight: "pencil-outline",
						onPress: () => showEditName(),
					},
					{
						icon: "email-outline",
						title: t("account.email"),
						description: email,
					},
				],
			},
			...(isDevelopment
				? [
						{
							sectionTitle: t("account.dev"),
							items: [
								{
									onPress: () => {
										router.push("/sandbox");
									},
									icon: "code-json",
									title: t("account.sandbox"),
								},
								{
									onPress: () => toggleDialog("APP_SELECTION"),
									icon: "apps",
									title: "Switch app",
									description: `Current app: ${buildType}`,
								},
								{
									onPress: () => {
										router.push("/onboarding");
									},
									icon: "hand-wave-outline",
									title: t("account.redoOnboarding"),
								},
								{
									onPress: () => resetGlobalStore(),
									icon: "database-refresh",
									title: "Reset global store",
								},
							],
						},
					]
				: []),
			{
				sectionTitle: t("account.about"),
				items: [
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.termsOfServiceClicked,
							});
							Linking.openURL(tosLink);
						},
						icon: "gavel",
						title: t("account.termsOfService"),
						iconRight: "open-in-new",
					},
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.privacyPolicyClicked,
							});
							Linking.openURL(privacyLink);
						},
						icon: "eye-outline",
						title: t("account.privacyPolicy"),
						iconRight: "open-in-new",
					},
					...(aiToolsTosLink
						? [
								{
									onPress: () => {
										explicitCallTrackCustomEvent({
											eventName: events.aiToolsTosClicked,
										});
										Linking.openURL(aiToolsTosLink);
									},
									icon: "robot-outline",
									title: t("account.aiToolsTos"),
									iconRight: "open-in-new",
								},
							]
						: []),
					{
						onPress: async () => {
							explicitCallTrackCustomEvent({
								eventName: events.contactSupportClicked,
								username,
								email,
							});

							if (contactSupportUrl && isSafeUrl(contactSupportUrl)) {
								Linking.openURL(contactSupportUrl);
								return;
							}

							const platform = getPlatform();
							const subject = `${buildType} ${t("account.support")} | ${platform} | ${username}`;
							const body = t("account.supportEmailBody", { username, email });

							if (isWeb) {
								await copyToClipboard(
									supportEmail,
									t("common.copiedToClipboard"),
								);
							}

							composeMessage(subject, body, supportEmail);
						},
						icon: "help-circle-outline",
						title: t("account.contactSupport"),
						rightText:
							isWeb && !isSafeUrl(contactSupportUrl) ? supportEmail : undefined,
						iconRight: "open-in-new",
					},
					{
						onPress: () =>
							handleFeedbackNavigation(() => toggleDialog("FEEDBACK")),
						icon: "message-text-outline",
						title: t("common.leaveFeedback"),
					},
					...(showFaqs
						? [
								{
									onPress: isWeb
										? () => toggleDialog("FAQS")
										: () => {
												router.push("/faqs");
											},
									icon: "message-question-outline",
									title: t("account.frequentlyAskedQuestions"),
								},
							]
						: []),
					{
						onPress: () => toggleDialog("SIGN_OUT"),
						icon: "logout",
						title: t("account.signOut"),
					},
					{
						onPress: toggleShowDeleteFeedbackDialog,
						icon: "account-remove-outline",
						title: t("account.deleteAccount"),
					},
				],
			},
			{
				sectionTitle: t("account.platform"),
				items: [
					...(showPartnersSection
						? [
								{
									onPress: () => {
										Linking.openURL(PROD_CORE_URL);
									},
									icon: "web",
									title: t("account.partnerships"),
									iconRight: "open-in-new",
								},
							]
						: []),
					{
						icon: "unfold-more-vertical",
						title: `${appVersion}${buildNumber ? ` (${buildNumber})` : ""}`,
					},
				],
			},
		];
	};
	return {
		isLoading: false,
		getAccountSections,
		usernameValue: username,
		actualUsernameValue: username,
	};
};

export default useAccountSettings;
